/**
 * cart-upsell
 *
 * Custom element para la sección de upsell del cart drawer.
 * - Botón "Añadir" en productos de una sola variante => agrega directamente.
 * - Botón "Elegir" en productos con varias variantes => abre un modal selector
 *   con nombre, precio y stock por variante (las agotadas quedan deshabilitadas).
 *
 * Reutiliza routes.cart_add_url y refresca el cart drawer pidiendo la sección
 * `cart-drawer`, siguiendo el patrón de cart-drawer.js (renderContents).
 */
class CartUpsell extends HTMLElement {
  constructor() {
    super();

    this.modal = this.querySelector('[data-upsell-modal]');
    this.modalVariants = this.querySelector('[data-upsell-modal-variants]');
    this.modalTitle = this.querySelector('[data-upsell-modal-title]');
    this.modalImage = this.querySelector('[data-upsell-modal-image]');
    this.modalError = this.querySelector('[data-upsell-modal-error]');
    this.modalSubmit = this.querySelector('[data-upsell-modal-submit]');
    this.selectedVariantId = null;
    this.activeTrigger = null;

    this.addEventListener('click', this.onClick.bind(this));

    if (this.modal) {
      this.modal.querySelectorAll('[data-upsell-modal-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeModal());
      });
      this.modalSubmit?.addEventListener('click', () => {
        if (this.selectedVariantId) this.addToCart(this.selectedVariantId, this.modalSubmit);
      });
      this.modalVariants?.addEventListener('click', this.onVariantClick.bind(this));
      document.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape' && !this.modal.hasAttribute('hidden')) this.closeModal();
      });
    }
  }

  onClick(event) {
    const addBtn = event.target.closest('[data-upsell-add]');
    if (addBtn) {
      event.preventDefault();
      this.addToCart(addBtn.dataset.variantId, addBtn);
      return;
    }

    const chooseBtn = event.target.closest('[data-upsell-choose]');
    if (chooseBtn) {
      event.preventDefault();
      this.openModal(chooseBtn);
    }
  }

  openModal(trigger) {
    let variants;
    try {
      variants = JSON.parse(trigger.dataset.variants);
    } catch (e) {
      console.error('cart-upsell: invalid variant data', e);
      return;
    }
    if (!Array.isArray(variants) || variants.length === 0) return;

    const title = trigger.dataset.productTitle || '';
    const image = trigger.dataset.productImage || '';

    this.activeTrigger = trigger;
    this.selectedVariantId = null;
    this.modalError.setAttribute('hidden', '');
    this.modalSubmit.disabled = true;

    this.modalTitle.textContent = title;
    if (image) {
      this.modalImage.src = image;
      this.modalImage.alt = title;
      this.modalImage.removeAttribute('hidden');
    } else {
      this.modalImage.setAttribute('hidden', '');
    }

    this.modalVariants.innerHTML = variants
      .map((variant) => this.variantOptionHTML(variant))
      .join('');

    this.modal.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');
    // Foco al diálogo para accesibilidad.
    this.modal.querySelector('.cart-upsell-modal__dialog')?.focus();
  }

  escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  variantOptionHTML(variant) {
    const soldOut = !variant.available;
    // Stock disponible solo cuando Shopify gestiona inventario.
    let stockLabel = '';
    if (variant.available && variant.inventory_management === 'shopify' && variant.inventory_quantity > 0) {
      stockLabel = `<span class="cart-upsell-modal__variant-stock">${(window.upsellStrings?.stockLeft || '__QTY__ disponibles').replace(
        '__QTY__',
        variant.inventory_quantity
      )}</span>`;
    } else if (soldOut) {
      stockLabel = `<span class="cart-upsell-modal__variant-stock cart-upsell-modal__variant-stock--out">${
        window.upsellStrings?.soldOut || 'Agotado'
      }</span>`;
    }

    return `
      <label class="cart-upsell-modal__variant${soldOut ? ' cart-upsell-modal__variant--disabled' : ''}">
        <input
          type="radio"
          name="cart-upsell-variant"
          value="${variant.id}"
          class="cart-upsell-modal__variant-input"
          ${soldOut ? 'disabled' : ''}
        >
        <span class="cart-upsell-modal__variant-name">${this.escapeHTML(variant.title)}</span>
        <span class="cart-upsell-modal__variant-meta">
          <span class="cart-upsell-modal__variant-price">${this.escapeHTML(variant.price)}</span>
          ${stockLabel}
        </span>
      </label>
    `;
  }

  onVariantClick(event) {
    const input = event.target.closest('.cart-upsell-modal__variant-input');
    if (!input || input.disabled) return;
    this.selectedVariantId = input.value;
    this.modalError.setAttribute('hidden', '');
    this.modalSubmit.disabled = false;
    this.modalVariants.querySelectorAll('.cart-upsell-modal__variant').forEach((el) => {
      el.classList.toggle('cart-upsell-modal__variant--selected', el.contains(input));
    });
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');
    this.activeTrigger?.focus();
  }

  addToCart(variantId, button) {
    if (!variantId || button.classList.contains('loading')) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];

    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sectionsToRender,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Error de Shopify (p.ej. sin stock).
          this.showAddError(button, response.description || response.message);
          return;
        }

        this.closeModal();

        // Refresca el cart drawer con las secciones devueltas.
        if (cartDrawer && response.sections && response.sections['cart-drawer']) {
          cartDrawer.renderContents(response);
        } else if (cartDrawer) {
          cartDrawer.open();
        }

        // Notifica al resto del tema (barra de envío, etc.).
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', productVariantId: variantId });
        }
      })
      .catch((e) => {
        console.error('cart-upsell: add to cart failed', e);
        this.showAddError(button, window.upsellStrings?.error);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      });
  }

  showAddError(button, message) {
    if (this.modal && !this.modal.hasAttribute('hidden')) {
      this.modalError.textContent = message || window.upsellStrings?.error || '';
      this.modalError.removeAttribute('hidden');
    } else if (message) {
      // Feedback simple en el botón si no hay modal abierto.
      const original = button.querySelector('.cart-upsell__add-label')?.textContent;
      const label = button.querySelector('.cart-upsell__add-label');
      if (label) {
        label.textContent = window.upsellStrings?.error || message;
        setTimeout(() => {
          label.textContent = original;
        }, 2000);
      }
    }
  }
}

customElements.define('cart-upsell', CartUpsell);

/**
 * Bloqueo de scroll del fondo cuando el cart drawer está abierto.
 *
 * El `body.overflow-hidden` que aplica Dawn no siempre evita que la rueda del
 * ratón desplace la página de fondo en escritorio (sobre todo si el puntero
 * está fuera del drawer, o antes de que aparezca su barra de scroll interna).
 *
 * Esta mejora intercepta wheel/touchmove mientras el drawer está activo y solo
 * deja pasar el scroll cuando ocurre dentro de una zona desplazable del drawer.
 */
(function lockScrollWithCartDrawer() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  const isOpen = () => drawer.classList.contains('active');

  // ¿El evento ocurre dentro de una zona del drawer que realmente puede scrollear?
  const scrollableInsideDrawer = (target) => {
    const inner = target instanceof Element ? target.closest('.drawer__inner, cart-drawer-items, .cart-upsell__scroller, .cart-upsell-modal__dialog') : null;
    return Boolean(inner) && drawer.contains(inner);
  };

  const onWheel = (event) => {
    if (!isOpen()) return;
    // Si el modal de variantes está abierto, dejar que su propio dialog scrollee.
    if (scrollableInsideDrawer(event.target)) return;
    event.preventDefault();
  };

  // wheel y touchmove deben ser no-pasivos para poder llamar preventDefault().
  document.addEventListener('wheel', onWheel, { passive: false });
  document.addEventListener('touchmove', onWheel, { passive: false });
})();

/**
 * Sincroniza el estado vacío/no-vacío del cart drawer.
 *
 * Al añadir el primer producto desde un carrito vacío, la clase `is-empty` del
 * <cart-drawer> debe quitarse para que el drawer pase del layout "vacío"
 * (cabecera oculta, contenido centrado) al de "con productos" (lista + upsell).
 * Esto refuerza la lógica de Dawn por si el cambio de clase no se aplica a tiempo.
 */
(function syncCartDrawerEmptyState() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    const count = event?.cartData?.item_count;
    if (typeof count !== 'number') {
      // Sin dato fiable: deducir de la presencia de líneas en el DOM.
      const hasItems = Boolean(drawer.querySelector('.cart-item'));
      drawer.classList.toggle('is-empty', !hasItems);
      return;
    }
    drawer.classList.toggle('is-empty', count === 0);
  });
})();
